<template>
    <div class="card" v-if="voucherObj">
        <div class="card-header justify-content-between align-items-center pt-2">
            <h2 class="card-title">{{ voucherObj.name }}</h2>
            <button class="btn btn-sm btn-outline-secondary" title="Back" @click="gotToListPage">
                <i class="fas fa-times"></i>
            </button>
        </div>
        <div class="card-body">
            <template v-if="voucher">
                <p>
                    <b>Date: </b> {{ voucher.date }}
                </p>
                <p>
                    <b>Voucher Number: </b> {{ voucher.voucher_no }}
                </p>

                <table class="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Account Head</th>
                            <th>Description</th>
                            <th>Party Name</th>
                            <th>Debit</th>
                            <th>Credit</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="ledger in voucher.ledgers">
                            <td>
                                {{ ledger.head.name }}
                            </td>
                            <td>
                                {{ ledger.note }}
                            </td>
                            <td>
                                {{ ledger.party ? ledger.party.name : '' }}
                            </td>
                            <td class="text-right px-2">
                                {{ ledger.debit ? commaFormat(ledger.debit) : '' }}
                            </td>
                            <td class="text-right px-2">
                                {{ ledger.credit ? commaFormat(ledger.credit) : '' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div class="my-2">
                    <p>
                        <b>Total Amount: </b> {{ commaFormat(voucher.total_amount) }}
                    </p>
                    <p v-if="voucher.note">
                        <b>Voucher Note: </b> {{ voucher.note }}
                    </p>

                    <div class="form-group mb-2">
                        <label for="approval-note" class="mb-1 fw-bolder">
                            {{ isCancellationPage ? 'Cancellation' : 'Approval' }} Note:
                        </label>
                        <textarea
                            name="approval-note"
                            id="approval-note"
                            class="form-control"
                            rows="5"
                            v-model="approvalNote"
                        ></textarea>
                    </div>

                    <div v-if="approvalLogs && approvalLogs.length > 0">
                        <div
                            class="alert py-1 border px-2"
                            v-for="log in approvalLogs"
                            :class="approvalLogClasses(log.status)"
                        >
                            <p class="">
                                Status: <strong class="capitalize">{{ log.status }}</strong>
                            </p>
                            <p class="text-dark font-small-3">
                                Approved By: <strong class="capitalize">{{ log.approved_by_user.name }}</strong>
                            </p>
                            <p class="text-dark" v-if="log.note">
                                Note: {{ log.note }}
                            </p>
                        </div>
                    </div>

                    <div v-if="isCancellationPage" class="d-flex align-items-center justify-content-end gap-2 mx-2">
                        <button
                            :disabled="disableButton"
                            class="btn btn-danger"
                            @click="handleApprovalOrRejection('cancelled')"
                        >
                            Cancel Voucher
                        </button>
                        <button
                            class="btn btn-outline-secondary"
                            @click="gotToListPage"
                        >
                            Back
                        </button>
                    </div>

                    <div v-else class="d-flex align-items-center justify-content-end gap-2 mx-2">
                        <button :disabled="disableButton" class="btn btn-success" @click="handleApprovalOrRejection()">Approve</button>
                        <button :disabled="disableButton" class="btn btn-info" @click="handleApprovalOrRejection('return')">Return</button>
                        <button :disabled="disableButton" class="btn btn-danger" @click="handleApprovalOrRejection('rejected')">Reject</button>
                    </div>
                </div>
            </template>
        </div>
    </div>

    <div v-else>
        <div class="alert alert-danger text-center py-4">
            Invalid voucher please check again
        </div>
    </div>
</template>

<script setup>
import {useVoucherPrint} from "@/services/utils/voucherPrint";
import {computed, inject, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import figureFormatter from "@/services/utils/figureFormatter";
import handleJournal from "@/services/modules/journal";

const { vouchers } = useVoucherPrint();
const voucher = ref(null)
const approvalNote = ref('')
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')
const {commaFormat} = figureFormatter();
const { handleApproval } = handleJournal();

const voucherObj = computed(() => {
    if (vouchers[$route.query.type]) {
        return vouchers[$route.query.type];
    }

    return null;
})

const isCancellationPage = computed(() => {
    if ($route.query.status && $route.query.status === 'handle-cancellation') {
        return true;
    }

    return false;
})

const disableButton = computed(() => {
    if (!voucher.value) {
        return true;
    }

    if (isCancellationPage.value && $route.query.redirect_name === 'payment-requisition-disbursement') {
        return voucher.value.status !== 'pa_approved'
    }

    if (isCancellationPage.value) {
        return ! ['approved', 'pending'].includes(voucher.value.status)
    }

    return voucher.value.status !== 'pending_approval'
})

const approvalLogs = computed(() => {

    if (! isCancellationPage.value) {
        return []
    }

    return voucher.value.ledger_approval_logs ?? [];
})

const approvalLogClasses = (status) => {
    if (status === 'approved') {
        return 'alert-success border-success'
    }

    if (['rejected', 'cancelled'].includes(status)) {
        return 'alert-danger border-danger'
    }

    return 'alert-info border-info'
}

const getVoucher = () => {
    voucherObj.value.vMethod($route.params.voucherId + '?with_log=1')
        .then(res => {
            voucher.value = res.data;
        })
        .catch(() => {
            showError("Something went wrong please try again later")
        })
}

const gotToListPage = () => {
    const fallbackRouteName = isCancellationPage.value ? 'voucher-list' : 'voucher-approval-list'
    $router.push({
        name: $route.query.redirect_name ? $route.query.redirect_name : fallbackRouteName,
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: {
            start: $route.query.start,
            end: $route.query.end
        }
    })
}

const handleApprovalOrRejection = (status = 'approved') => {
    const note = approvalNote.value ? approvalNote.value.trim() : false;

    if (status !== 'approved' && !note) {
        showError(`Approval note is required if it is ${isCancellationPage.value ? 'cancelled' : 'rejected or return'}`);
        return;
    }

    const allowPaApproved = $route.query.redirect_name === 'payment-requisition-disbursement'

    handleApproval({
        company_id: $route.params.companyId,
        voucher_ids: [$route.params.voucherId],
        note: !note ? null : note,
        status,
        allow_pa_approved: allowPaApproved
    }).then(res => {
        showSuccess(res.message)
        gotToListPage()
    }).catch(err => {
        if(err.response.data) {
            showError(err.response.data.message);
            return;
        }

        showError('Something went wrong please try again later');
    })
}

onMounted(() => {
    if (! voucherObj.value) {
        return;
    }

    getVoucher();
})


</script>